import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Copy, RotateCcw, Trash2, Loader2, ImageOff, ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { getPendingRecords, retryRecord, discardRecord, subscribe } from '@/lib/offlineOutbox';
import { readCachedValue } from '@/lib/offlineCache';
import PageContainer from '@/components/PageContainer';

/**
 * Every field a rep can type in, in the order they'd expect to see them,
 * paired with a human label. Anything blank is skipped rather than shown as
 * an empty row, task: stuck captures (2026-09-08). Not the full
 * EDITABLE_FIELDS list from worker/lib/contacts.ts: a few internal-only
 * fields (email_note, zoominfo suggestions, calendar_link_id) are left out
 * on purpose, since nothing meaningful for a rep diagnosing a failed save
 * would ever be typed into them from this form.
 */
const DISPLAY_FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['company', 'Company'],
  ['title', 'Title'],
  ['email', 'Email'],
  ['phone', 'Cell'],
  ['office_phone', 'Office phone'],
  ['website', 'Website'],
  ['meeting_topics', 'Meeting topics'],
  ['internal_notes', 'Internal notes'],
  ['linkedin_url', 'LinkedIn'],
];

function showNameFor(showId) {
  if (!showId) return null;
  const shows = readCachedValue('shows', []);
  const show = shows.find((s) => s.id === showId);
  return show?.name || null;
}

function clipboardText(record) {
  const lines = [];
  const name = [record.payload?.first_name, record.payload?.last_name].filter(Boolean).join(' ');
  lines.push(name || 'Unnamed contact');
  for (const [field, label] of DISPLAY_FIELDS) {
    const value = record.payload?.[field];
    if (value) lines.push(`${label}: ${value}`);
  }
  const showName = showNameFor(record.payload?.show_id);
  if (showName) lines.push(`Show: ${showName}`);
  lines.push(`Photos: badge ${record.badgeBlob ? 'yes' : 'no'}, back of card ${record.badgeBackBlob ? 'yes' : 'no'}, person ${record.personBlob ? 'yes' : 'no'}`);
  lines.push(`Save attempted: ${format(new Date(record.createdAt), 'EEE MMM d, yyyy h:mm a')}`);
  lines.push(`Attempts rejected by server: ${record.serverAttemptCount || 0}`);
  lines.push(`Error: ${record.lastError || '(none recorded)'}`);
  return lines.join('\n');
}

function StuckCaptureCard({ record, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const name = [record.payload?.first_name, record.payload?.last_name].filter(Boolean).join(' ') || 'Unnamed contact';
  const showName = showNameFor(record.payload?.show_id);

  const handleRetry = async () => {
    setBusy(true);
    const result = await retryRecord(record.clientUuid);
    setBusy(false);
    if (result.ok) {
      toast.success(`${name} synced.`);
      onChanged();
    } else {
      toast.error(`Still failing: ${result.error}`);
      onChanged();
    }
  };

  const handleDiscard = async () => {
    setBusy(true);
    await discardRecord(record.clientUuid);
    setBusy(false);
    toast.success(`${name} discarded.`);
    onChanged();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(clipboardText(record));
      toast.success('Copied to clipboard.');
    } catch {
      toast.error('Could not copy. Your browser may not allow clipboard access here.');
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="bg-card rounded-2xl p-4 shadow-sm border border-destructive/20"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="font-semibold text-foreground">{name}</p>
          {showName && <p className="text-xs text-muted-foreground">{showName}</p>}
        </div>
        <span className="text-[10px] font-medium bg-destructive/10 text-destructive px-2 py-1 rounded-full shrink-0">
          {record.serverAttemptCount || 0} rejected attempt{record.serverAttemptCount === 1 ? '' : 's'}
        </span>
      </div>

      <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-2.5 mb-3 flex gap-2">
        <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
        <p className="text-xs text-destructive font-medium break-words">{record.lastError || 'No error text was recorded.'}</p>
      </div>

      <div className="space-y-1 mb-3">
        {DISPLAY_FIELDS.map(([field, label]) => {
          const value = record.payload?.[field];
          if (!value) return null;
          return (
            <div key={field} className="flex text-xs gap-2">
              <span className="text-muted-foreground w-28 shrink-0">{label}</span>
              <span className="text-foreground break-words">{value}</span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          {record.badgeBlob ? <ImageIcon className="w-3.5 h-3.5" /> : <ImageOff className="w-3.5 h-3.5" />}
          Badge
        </span>
        <span className="flex items-center gap-1">
          {record.badgeBackBlob ? <ImageIcon className="w-3.5 h-3.5" /> : <ImageOff className="w-3.5 h-3.5" />}
          Back of card
        </span>
        <span className="flex items-center gap-1">
          {record.personBlob ? <ImageIcon className="w-3.5 h-3.5" /> : <ImageOff className="w-3.5 h-3.5" />}
          Person photo
        </span>
      </div>

      <p className="text-[11px] text-muted-foreground mb-3">
        Save attempted {format(new Date(record.createdAt), 'EEE MMM d, h:mm a')}
      </p>

      {confirmingDiscard ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-secondary/40 rounded-xl">
          <p className="text-xs font-medium text-foreground mb-2">Discard this capture for good? This cannot be undone.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmingDiscard(false)} className="flex-1 min-h-8 text-xs">Cancel</Button>
            <Button size="sm" onClick={handleDiscard} disabled={busy} className="flex-1 min-h-8 text-xs bg-destructive hover:bg-destructive/90 text-white">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Yes, discard'}
            </Button>
          </div>
        </motion.div>
      ) : (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy} className="flex-1 min-h-9 text-xs">
            <Copy className="w-3.5 h-3.5 mr-1.5" />
            Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleRetry} disabled={busy} className="flex-1 min-h-9 text-xs">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Retry</>}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setConfirmingDiscard(true)} disabled={busy} className="flex-1 min-h-9 text-xs text-destructive border-destructive/30 hover:bg-destructive/10">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Discard
          </Button>
        </div>
      )}
    </motion.div>
  );
}

export default function StuckCaptures() {
  const [records, setRecords] = useState(null);

  const reload = async () => {
    const all = await getPendingRecords();
    setRecords(all.filter((r) => r.stuck));
  };

  useEffect(() => {
    reload();
    return subscribe(reload);
  }, []);

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer className="px-4 py-6">
        {/* Global "Back" button (task: 2026-09-25), replacing the old round
            arrow icon. Restructured into the same title+subtitle group
            every other page's header uses, rather than the manual ml-11
            indent this page used to align the subtitle under a small icon
            that no longer exists in that shape. */}
        <div className="flex items-start gap-3 mb-5">
          <Link to="/"><Button variant="outline" className="min-h-9 px-4 text-sm">Back</Button></Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">Stuck Captures</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              These are on this device only, saved but rejected by the server every time they were tried.
              Fix the problem the error names, tap Retry, or copy the details out and discard it.
            </p>
          </div>
        </div>

        {records === null ? (
          <div className="space-y-3">
            {Array(2).fill(0).map((_, i) => (
              <div key={i} className="bg-card rounded-2xl p-4 animate-pulse h-40" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16">
            <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">Nothing stuck</p>
            <p className="text-sm text-muted-foreground/60 mt-1">Every queued capture is either synced or still waiting for signal.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {records.map((record) => (
                <StuckCaptureCard key={record.clientUuid} record={record} onChanged={reload} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </PageContainer>
    </div>
  );
}
